import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, CreditCard, FileText, Bell, Calendar, Home, Users, FileText as FileTextIcon, Newspaper } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTexts, useSiteInfo } from '../../hooks/useTexts';
import { clientApiService } from '../../services/clientApi';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  const { getSiteInfo } = useSiteInfo();

  const resolveLabel = (key: string, fallback: string) => {
    const value = getText(key);
    return value === key ? fallback : value;
  };

  let navigation = [
    { name: resolveLabel('nav.home', 'Accueil'), href: '/' },
    { name: resolveLabel('nav.projects', 'Projets'), href: '/projects' },
    { name: resolveLabel('nav.news', 'Actualités'), href: '/news' },
    { name: resolveLabel('nav.about', 'À propos'), href: '/about' },
    { name: resolveLabel('nav.contact', 'Contact'), href: '/contact' },
  ];

  if(isAuthenticated){
    navigation = [
      { name: getText('nav.dashboard'), href: '/dashboard' },
      { name: getText('nav.subscriptions'), href:'/subscriptions'},
      { name: getText('nav.schedules'), href:'/echeanciers'},
      { name: getText('nav.payments'), href:'/payments'},
      { name: getText('nav.documents'), href:'/documents'},
      { name: resolveLabel('nav.news', 'Actualités'), href: '/news' },
    ];
  }

  const isActive = (path: string) => location.pathname === path;

  // Charger les notifications non lues
  useEffect(() => {
    const loadUnreadNotifications = async () => {
      if (isAuthenticated && user) {
        try {
          const response = await clientApiService.getMyNotifications();
          const notifications = response.results || [];
          // Filtrer côté client comme dans NotificationsPage
          const unreadCount = notifications.filter(n => !n.lu).length;
          setUnreadNotificationsCount(unreadCount);
        } catch (error) {
          console.error('Erreur lors du chargement des notifications:', error);
        }
      }
    };

    loadUnreadNotifications();
    
    // Écouter les événements de mise à jour des notifications
    const handleNotificationUpdate = () => {
      loadUnreadNotifications();
    };
    
    // Écouter l'événement personnalisé
    window.addEventListener('notifications-updated', handleNotificationUpdate);
    
    // Rafraîchir le compteur toutes les 30 secondes (fallback)
    const interval = setInterval(loadUnreadNotifications, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-updated', handleNotificationUpdate);
    };
  }, [isAuthenticated, user]);

  // Gérer le clic à l'extérieur pour fermer les menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Fermer le menu utilisateur desktop
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      
      // Fermer le menu mobile
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              {(() => {
                const logoInfo = getSiteInfo('header.company_logo');
                const logoUrl = logoInfo?.image_url_final;
                const companyName = getText('header.company_name');
                
                if (logoUrl) {
                  return (
                    <>
                      <img 
                        src={import.meta.env.VITE_API_BASE_URL?.replace('/api', '') + logoUrl} 
                        alt={companyName}
                        className="h-16 w-auto object-contain"
                      />
                      
                    </>
                  );
                }
                return <span className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors">{companyName}</span>;
              })()}
            </Link>
          </div>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium transition-colors relative ${
                  isActive(item.href)
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Actions Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                {/* Cloche des notifications */}
                <Link
                  to="/notifications"
                  className="relative p-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                    </span>
                  )}
                </Link>
                
                {/* Menu utilisateur */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium">{user?.prenoms}</span>
                  </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{user?.prenoms} {user?.nom}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                    
                    {/* Liens principaux */}
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Home className="inline w-4 h-4 mr-2" />
                      {getText('nav.dashboard')}
                    </Link>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Liens utiles */}
                    <Link
                      to="/subscriptions"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <FileText className="inline w-4 h-4 mr-2" />
                      {getText('nav.subscriptions')}
                    </Link>
                    
                    <Link
                      to="/echeanciers"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Calendar className="inline w-4 h-4 mr-2" />
                      {getText('nav.schedules')}
                    </Link>
                    
                    <Link
                      to="/payments"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <CreditCard className="inline w-4 h-4 mr-2" />
                      {getText('nav.payments')}
                    </Link>
                    
                    <Link
                      to="/documents"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <FileText className="inline w-4 h-4 mr-2" />
                      {getText('nav.documents')}
                    </Link>
                    
                    <Link
                      to="/devis-requests"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <FileTextIcon className="inline w-4 h-4 mr-2" />
                      {getText('devis.title')}
                    </Link>
                    
                    <Link
                      to="/contact-persons"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Users className="inline w-4 h-4 mr-2" />
                      {getText('contact_persons.title')}
                    </Link>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Liens de gestion */}
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="inline w-4 h-4 mr-2" />
                      {getText('nav.profile')}
                    </Link>
                    
                    <button
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="inline w-4 h-4 mr-2" />
                      {getText('nav.logout')}
                    </button>
                  </div>
                )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-blue-600 px-4 py-2 text-sm font-medium transition-colors"
                >
                  {getText('nav.login')}
                </Link>
                <Link
                  to="/contact"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  {getText('nav.contact')}
                </Link>
              </div>
            )}
          </div>

          {/* Menu Mobile */}
          <div className="md:hidden flex items-center space-x-2">
            {isAuthenticated && (
              <Link
                to="/notifications"
                className="relative p-2 text-gray-700 hover:text-blue-600 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </span>
                )}
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-blue-600 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200" ref={mobileMenuRef}>
            <div className="space-y-2">
              {!isAuthenticated && navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {isAuthenticated ? (
                <div className="border-t pt-2 mt-2">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{user?.prenoms} {user?.nom}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                  
                  {/* Liens principaux */}
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Home className="inline w-4 h-4 mr-2" />
                    {getText('nav.dashboard')}
                  </Link>
                  
                  {/* Liens utiles */}
                  <Link
                    to="/subscriptions"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FileText className='inline w-4 h-4 mr-2' />
                    {getText('nav.subscriptions')}
                  </Link>
                  
                  <Link
                    to="/echeanciers"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Calendar className='inline w-4 h-4 mr-2' />
                    {getText('nav.schedules')}
                  </Link>
                  
                  <Link
                    to="/payments"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <CreditCard className='inline w-4 h-4 mr-2' />
                    {getText('nav.payments')}
                  </Link>
                  
                  <Link
                    to="/documents"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FileText className='inline w-4 h-4 mr-2' />
                    {getText('nav.documents')}
                  </Link>
                  
                  <Link
                    to="/devis-requests"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FileTextIcon className='inline w-4 h-4 mr-2' />
                    {getText('devis.title')}
                  </Link>
                  
                  <Link
                    to="/contact-persons"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Users className='inline w-4 h-4 mr-2' />
                    {getText('contact_persons.title')}
                  </Link>

                  <Link
                    to="/news"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Newspaper className='inline w-4 h-4 mr-2' />
                    {resolveLabel('nav.news', 'Actualités')}
                  </Link>

                  <Link
                    to="/news"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <Newspaper className="inline w-4 h-4 mr-2" />
                    {resolveLabel('nav.news', 'Actualités')}
                  </Link>
                  
                  {/* Liens de gestion */}
                  <Link
                    to="/profile"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className='inline w-4 h-4 mr-2' />
                    {getText('nav.profile')}
                  </Link>
                  
                  <button
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="inline w-4 h-4 mr-2" />
                    {getText('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="border-t pt-2 mt-2 space-y-2">
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getText('nav.login')}
                  </Link>
                  <Link
                    to="/contact"
                    className="block mx-3 py-2 text-center bg-blue-600 text-white rounded-lg text-base font-medium hover:bg-blue-700 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {getText('nav.contact')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}